<template>
  <div
    :class="{'is-active': active}"
    class="modal"
  >
    <div class="modal-background" />
    <div
      :style="modalStyle"
      :class="[{ 'modal-card--fullscreen': fullscreen }, `is-${appearance}`]"
      class="modal-card"
    >
      <div
        v-if="fullscreen"
        class="modal-close-button"
        @click="$emit('close')"
      >
        <i class="icon-exit-custom" />
      </div>
      <header
        v-if="hasHeader"
        v-show="!fullscreen"
        :class="{'modal-card-head--bordered': headerBordered}"
        class="modal-card-head"
      >
        <div
          v-if="$slots.customHeader"
          class="custom-header-container flex-1"
        >
          <slot name="customHeader" />
        </div>
        <p
          v-else
          :class="[`modal-card-title--${titleSize}`,
                   {'modal-card-title--info': helpId !== '' && user?.profile.helpdesk === 'S'}]"
          class="modal-card-title"
        >
          <span>{{ title }}</span>
          <IconButton
            v-if="helpId !== '' && user?.profile.helpdesk === 'S'"
            :size="20"
            :padding="0"
            :color="helpColor"
            icon="info"
            background="transparent"
            class="has-margin-left-1"
            @click="handleHelpdesk"
          />
        </p>
        <div
          v-if="exitButton"
          class="modal-close-button"
          @click="$emit('close')"
        >
          <i class="icon icon-cancel" />
        </div>
      </header>
      <div
        v-else-if="!hasHeader"
        class="modal-empty-header"
      />
      <section
        :class="[
          {'modal-card-body--radius': !footerPadding && !hasFooter},
          {'modal-card-body--no-overflow': nobodyOverflow},
        ]"
        class="modal-card-body"
      >
        <div
          v-if="$slots.default"
          class="body-content"
        >
          <slot />
        </div>
      </section>
      <footer
        v-if="hasFooter && !$slots.customFooter"
        :class="{
          'modal-card-foot--empty': !hasFooterButtons,
          'has-padding-3': footPadding,
          'modal-card-foot--bordered': footerBordered,
        }"
        class="modal-card-foot"
      >
        <div
          v-if="hasFooterButtons"
          class="flex-1 justify-space-between is-flex align-center"
        >
          <div>
            <span
              v-if="resetButton"
              :class="[resetButtonClass]"
              class="modal-reset-button"
              @click="$emit('reset')"
            >
              {{ resetLabel ||$t('comps.SimpleModal.reset') }}
            </span>
            <!-- <SingleButton
              v-if="resetButton"
              outlined
              borderless
              @click="$emit('reset')">
              {{ resetLabel ||$t('comps.SimpleModal.reset') }}
            </SingleButton> -->
          </div>
          <div class="is-flex">
            <SingleButton
              v-if="cancelButton"
              :height="buttonsSize"
              outlined
              borderless
              light
              @click="$emit('close')"
            >
              {{ cancelLabel || $t('comps.SimpleModal.cancel') }}
            </SingleButton>
            <SingleButton
              v-if="acceptButton"
              :disabled="applyDisabled"
              :height="buttonsSize"
              :appearance="applyButtonAppearance"
              :shadow="applyButtonAppearance === 'primary'"
              :borderless="applyButtonAppearance === 'danger'"
              @click="$emit('apply')"
            >
              {{ applyLabel || $t('comps.SimpleModal.apply') }}
            </SingleButton>
          </div>
        </div>
      </footer>
      <slot
        v-else-if="hasFooter && $slots.customFooter"
        name="customFooter"
      />
      <div
        v-else-if="!hasFooter"
        :class="[{'modal-empty-footer--no-padding': !footerPadding}]"
        class="modal-empty-footer"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { useRootStore } from '@/stores/root';
import SingleButton from '@/components/buttons/SingleButton.vue';
import IconButton from '@/components/buttons/IconButton.vue';

export default {
  name: 'SimpleModal',
  components: {
    SingleButton,
    IconButton,
  },
  props: {
    active: { type: Boolean, default: false },
    resetButton: { type: Boolean, default: false },
    exitButton: { type: Boolean, default: false },
    cancelButton: { type: Boolean, default: true },
    nobodyOverflow: { type: Boolean, default: false },
    acceptButton: { type: Boolean, default: true },
    applyDisabled: { type: Boolean, default: false },
    headerBordered: { type: Boolean, default: false },
    footerBordered: { type: Boolean, default: false },
    fullscreen: { type: Boolean, default: false },
    hasHeader: { type: Boolean, default: true },
    hasFooter: { type: Boolean, default: true },
    hasFooterButtons: { type: Boolean, default: true },
    title: { type: String, default: '' },
    applyLabel: { type: String, default: null },
    cancelLabel: { type: String, default: null },
    resetLabel: { type: String, default: null },
    width: { type: Number, default: null },
    percentageWidth: { type: String, default: null },
    footPadding: { type: Boolean, default: false },
    appearance: {
      type: String,
      default: 'primary',
      validator: val => ['primary', 'secondary'].includes(val),
    },
    resetButtonClass: {
      type: String, default: '',
    },
    buttonsSize: { type: Number, default: 32 },
    titleSize: {
      type: String,
      default: 'normal',
      validator: val => ['normal', 'big'].includes(val),
    },
    helpId: { type: String, default: '' },
    helpColor: { type: String, default: '#C7C7C7' },
    footerPadding: { type: Boolean, default: true },
    applyButtonAppearance: { type: String, default: 'primary' },
  },
  emits: ['close', 'reset', 'apply'],
  computed: {
    // Vuex mapGetters(['user']) → Pinia rootStore.
    ...mapState(useRootStore, ['user']),
    modalStyle() {
      return Object.assign(
        !!this.width && !this.fullscreen && { width: `${this.width}px` },
        this.percentageWidth && { width: this.percentageWidth },
      );
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        if (this.fullscreen && newVal) document.querySelector('html').style.overflow = 'hidden';
        else document.querySelector('html').style.overflow = '';
      },
    },
  },
  methods: {
    handleHelpdesk() {
      // Vite: process.env.VUE_APP_* → import.meta.env.VITE_*.
      window.open(`${import.meta.env.VITE_HELPDESK_URL}?id=${this.helpId}`);
    },
  },
};
</script>

<style lang="scss">

.modal-background {
  background-color: $overlay-background-color;
}

.modal {
  .modal-card .modal-close-button {
    cursor: pointer;
    align-self: start;
  }
  .modal-card {
    &--fullscreen {
      position: relative;
      max-height: none !important;
      height: 100vh;
      width: 100vw;
      margin: 0 !important;
    }
  }
  .modal-empty-header {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    padding-top: 8px;
    background-color: $white;
  }
  .modal-empty-footer {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding-bottom: 8px;
    background-color: $white;
    &--no-padding {
      padding-bottom: 0;
    }
  }
}
.modal-card-foot {
  height: 62px;

  &--empty {
    padding: 5px;
  }

  .modal-reset-button {
    cursor: pointer;
    font-size: $size-9;
    font-weight: $weight-medium;
    color: $grey-gainsboro;
  }
}

.modal-card-body {
  &--radius {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  &--no-overflow{
    overflow:hidden !important;
  }
}

.modal .is-secondary {
  &.modal-card .modal-card-head{
    height: 50px;
    padding: $modal-card-head-padding;
    align-items: center;
    border-bottom: 0;
    &--bordered {
      border-bottom: 1px solid #E2E6EB;
    }
  }
  &.modal-card .custom-header-container {
    overflow: hidden;
  }

  &.modal-card .modal-card-title {
    color: $black-gunmetal;
    font-size: $size-7;
    font-weight: $weight-medium;
    line-height: 21px;
    &--big {
      font-size: $size-5;
      line-height: 25px;
    }
    &--info {
      display: flex;
      align-items: center;
    }
  }

  &.modal-card .modal-close-button .icon{
    font-size: $size-9;
    font-weight: bold;
    color: $black-gunmetal;
    background-color: $grey-athens;
    border-radius: 50%;
  }

  &.modal-card.modal-card--fullscreen .modal-close-button {
    width: 46px;
    height: 46px;
    .icon-exit-custom:before,
    .icon-exit-custom:after{
      position: absolute;
      left: 23px;
      content: ' ';
      height: 45px;
      width: 2px;
      background-color: $black;
    }
    .icon-exit-custom:before {
      transform: rotate(45deg);
    }
    .icon-exit-custom:after {
      transform: rotate(-45deg);
    }
  }

  &.modal-card .modal-card-foot {
    border-top: 0;
    &--bordered {
      border-top: 1px solid #E2E6EB;
    }
  }
}

.modal .is-primary {
  &.modal-card .modal-card-head{
    background: $steel-gradient;
    height: 80px;
    align-items: flex-end;
  }

  &.modal-card.modal-card--fullscreen .modal-close-button {
    position: absolute;
    z-index: 1;
    top: 32px;
    right: 25px;
  }

  &.modal-card.modal-card--fullscreen .modal-close-button {
    width: 46px;
    height: 46px;
    .icon-exit-custom:before,
    .icon-exit-custom:after{
      position: absolute;
      left: 23px;
      content: ' ';
      height: 45px;
      width: 2px;
      background-color: $black;
    }
    .icon-exit-custom:before {
      transform: rotate(45deg);
    }
    .icon-exit-custom:after {
      transform: rotate(-45deg);
    }
  }

  &.modal-card .modal-close-button .icon{
    font-size: 18px;
    color: $white;
  }

  &.modal-card.modal-card--fullscreen .modal-card-body .body-content {
    height: 100vh;
  }

  &.modal-card .modal-card-foot{
    justify-content: space-between;
  }

}

</style>
